import React, { useEffect, useRef, useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Divider, Avatar, Form, Input, Spin, Select, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { formatImgPath, normalizeDateToLocal } from "../../../utils";
import PageHeader from "../../../components/PageHeader";
import { save, saveFile } from "../../../features/save/saveSlice";
import toast from "react-hot-toast";
import { getOrganizationDetails } from "../../../features/fetch/fetchSlice";
import citiesData from "../../../countries.json";
import dayjs from "dayjs";
import { addressTypeData } from "../../../data";

const EditAccount = ({ setIsEdit }) => {
  const { orgDetails } = useSelector((state) => state.fetch);
  const { user } = useSelector((state) => state.auth);

  const [imageSrc, setImageSrc] = useState(
    formatImgPath(
      orgDetails?.orgProfilePic == null
        ? "avatar.png"
        : orgDetails?.orgProfilePic
    )
  );

  const [accProfile, setAccProfile] = useState(null);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { saving } = useSelector((state) => state.save);

  const handleImageChange = async (e) => {
    let file = e.target.files[0];
    const formData = new FormData();
    formData.append("file", file);

    const res = await dispatch(saveFile(formData));

    if (res?.payload?.success) {
      await updateUserInfo(res.payload?.data?.result);
      await setAccProfile(res.payload?.data?.result);
    } else {
      toast.error(res?.payload?.messages?.message);
    }
  };

  const updateUserInfo = async (item) => {
    const res = await dispatch(
      save({
        url: "api/v1/public/update-organisation",
        orgProfilePic: item, 
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.messages?.message);
      await getOrganizationDetailsData();
    } else {
      toast.error(res?.payload?.messages?.message);
    }
  };

  async function getOrganizationDetailsData() {
    await dispatch(
      getOrganizationDetails({ 
        url: "api/v1/org/fetch-organisation",
      })
    );
  }

  const onFinish = async (data) => {
    data.orgRegistrationDate = normalizeDateToLocal(data?.orgRegistrationDate);
    const res = await dispatch(
      save({
        url: "api/v1/public/update-organisation",
        ...data,
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.messages?.message);
      await getOrganizationDetailsData();
      await setIsEdit(false);
    } else {
      toast.error(res?.payload?.messages?.message);
    }
  };

  const handleCancel = () => {
    setIsEdit(false);
  };

  const onSearchCountry = (value) => {};
  const [city, setCity] = useState([]);

  function selectedCountry(value) {
    const country = citiesData?.find((c) => c.name === value);
    setCity(country?.cities?.map((city) => city.name) || []);
  }

  const filterOptionCountry = (input, option) =>
    (option?.children?.toLowerCase() ?? "").includes(input.toLowerCase());

  useEffect(() => {
    setImageSrc(
      formatImgPath(
        orgDetails?.orgProfilePic == null
          ? "avatar.png"
          : orgDetails?.orgProfilePic
      )
    );
  }, [orgDetails]);
 
  return (
    <Form
      layout="vertical"
      ref={formRef}
      name="control-ref"
      onFinish={onFinish}
      form={form}
      initialValues={{
        ...orgDetails,
        orgRegistrationDate: orgDetails?.orgRegistrationDate
          ? dayjs(orgDetails.orgRegistrationDate)
          : null,
      }}
    >
      <div className="bg-white rounded-[10px] mt-[1.25rem] h-full  w-[100%] lg:p-10 p-3">
        <PageHeader header={"Edit Profile"} />
        <Divider />

        <div className="relative">
          <button className="mb-10">
            <Avatar size={150} src={imageSrc} />
            <div className="absolute left-[10%] bottom-10">
              <EditOutlined />
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
            />
          </button>
        </div>

        <div className="my-6">
          <span className="paragraph_paragraph_bb13 ">Company Details</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[.5rem]">
          <Form.Item
            name="orgName"
            label="Company Name"
            rules={[
              {
                message: "Please add company name",
              },
            ]}
          >
            <Input disabled className="input" placeholder="Company Name" />
          </Form.Item>
          <Form.Item
            name="orgEmail"
            label="Company Email"
            rules={[
              {
                message: "Please add Company Email",
              },
            ]}
          >
            <Input disabled className="input" />
          </Form.Item>
          <Form.Item
            name="orgTelephone"
            label="Company Number"
            rules={[
              {
                message: "Please add Phone Number",
              },
            ]}
          >
            <Input disabled className="input" />
          </Form.Item>

          <Form.Item
            name="orgBizTypeName"
            label="Business Type"
            rules={[
              {
                message: "Please add Business Type",
              },
            ]}
          >
            <Input disabled className="input" />
          </Form.Item>

          <Form.Item
            name="orgBusinessIndustryName"
            label="Industry"
            rules={[
              {
                message: "Please add industry",
              },
            ]}
          >
            <Input disabled className="input" />
          </Form.Item>

          <Form.Item
            name="orgRegNo"
            label="Business Registration No."
            rules={[
              {
                required: true,
                message: "Please add Business Registration No.",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            name="orgCountry"
            label="Country"
            rules={[{ required: true, message: "Please select a country" }]}
          >
            <Select
              showSearch
              placeholder="Country"
              size="large"
              className="reg-input"
              optionFilterProp="children"
              onChange={(value) => {
                selectedCountry(value);
                form.setFieldsValue({ orgCity: undefined });
              }}
              filterOption={filterOptionCountry}
            >
              {citiesData?.map((country) => (
                <Select.Option key={country.name} value={country.name}>
                  {country.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="City"
            name="orgCity"
            rules={[
              {
                required: true,
                message: "Please select a city",
              },
            ]}
          >
            <Select
              showSearch
              style={{
                height: "3.25rem",
              }}
              disabled={city?.length ? false : true}
              placeholder={"City"}
              size="large"
              className="reg-input"
              optionFilterProp="children"
              onSearch={onSearchCountry}
              filterOption={filterOptionCountry}
            >
              {city?.map((city) => {
                return (
                  <Select.Option required key={city} value={city}>
                    {city}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="orgRegistrationDate"
            rules={[
              {
                required: true,
                message: "Field is required",
              },
            ]}
            label="Registration Date"
          >
            <DatePicker format={"YYYY MMM DD"} className="input" />
          </Form.Item>

          <Form.Item
            className="table-form-item"
            name="orgKraPin"
            label="KRA Pin"
            rules={[
              {
                required: true,
                message: "Please add KRA pin",
              },
              {
                pattern: /^[A-Z0-9]{11}$/,
                message:
                  "Invalid Kra Pin format. A valid Kra Pin should be 11 alphanumeric characters.",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            name="orgPhysicalAdr"
            label="Physical Address"
            rules={[
              {
                required: true,
                message: "Physical Address is required to login",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>
        </div>
        <div className="my-6">
          <span className="paragraph_paragraph_bb13 ">Address Details</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[.5rem]">
          <Form.Item
            name="orgAddressType"
            className=""
            label={"Address Type"}
            rules={[
              {
                required: true,
                message: "Please select Address Types",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={
                addressTypeData?.length > 0 &&
                addressTypeData?.map((item) => ({
                  value: item?.value,
                  label: item?.label,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </Form.Item>

          <Form.Item
            label="Line 1"
            name="orgLine1"
            rules={[
              {
                required: true,
                message: "Line 1 is required",
              },
              {
                min: 3,
                max: 150,
                message: "Postal code must be >=3 and <=150 characters",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>
          <Form.Item
            label="Line 2"
            name="orgLine2"
            rules={[
              {
                required: false,
                message: "Line 2 is required",
              },
              {
                min: 3,
                max: 150,
                message: "Postal code must be >=3 and <=150 characters",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>
          <Form.Item
            label="Line 3"
            name="orgLine3"
            rules={[
              {
                required: false,
                message: "Line 3 is required",
              },
              {
                min: 3,
                max: 150,
                message: "Postal code must be >=3 and <=150 characters",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            label="Postal code"
            name="orgPostalAddress"
            rules={[
              {
                required: false,
                message: "Postal code is required",
              },
              {
                min: 3,
                max: 10,
                message: "Postal code must be >=3 and <=10 characters",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            label="State"
            name="orgState"
            rules={[
              {
                required: false,
                message: "State is required",
              },
              {
                min: 3,
                max: 50,
                message: "Postal code must be >=3 and <=50 characters",
              },
            ]}
          >
            <Input className="input" />
          </Form.Item>
        </div>

        <div className="flex lg:flex-row flex-col justify-between mt-[56px] mb-[48px]">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center gap-x-2">
            <div className="w-[200px]">
              <button
                key="back"
                type="button"
                onClick={handleCancel}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[200px]">
              <button
                key="submit"
                type="submit"
                className="cstm-btn-2"
                disabled={saving}
              >
                {saving ? <Spin /> : "Update"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default EditAccount;
